/**
 * Timed soldier status — "בבית עד יום שלישי 08:00", or "בגימלים עד להודעה
 * חדשה" when nobody knows yet.
 *
 * A manager sets where a soldier is and, if they know it, when they are back;
 * the status then expires by itself, with nothing to run and nothing to clear.
 * The end date is optional (רשות): a status set without one stays in force
 * until a commander changes it by hand — see {@link isIndefiniteStatus}. This
 * module is the single source of truth for reading such a status: whether it
 * is still running, which availability it forces while it is, and how it reads
 * in Hebrew. The matching engine is deliberately untouched — it keeps deciding
 * assignments from `Soldier.availability` alone.
 */
import { APP_CONFIG } from './config';
import {
  SOLDIER_AVAILABILITY_LABELS,
  TIMED_STATUS_AVAILABILITY,
  TIMED_STATUS_LABELS,
} from '../types/fleet';
import type { Soldier, SoldierAvailability, TimedStatus, TimedStatusKind } from '../types/fleet';

/**
 * The glyph a status is read by. A roster row is skimmed, not read, so every
 * screen that paints a status leads with the same emoji — 🟢 זמין, 🚚 במשימה,
 * 🏠 בבית. They live here rather than on the card because the quick-status
 * sheet paints them too, and two tables of emoji drift.
 */
export const AVAILABILITY_ICONS: Record<SoldierAvailability, string> = {
  available: '🟢',
  on_mission: '🚚',
  leave: '🌴',
  unavailable: '🔴',
};

/** A running timed status says where the soldier actually is — 🏠 over 🔴. */
export const TIMED_STATUS_ICONS: Record<TimedStatusKind, string> = {
  home: '🏠',
  leave: '🌴',
  sick: '🤒',
  course: '📘',
  guard: '🛡️',
  mission: '🚚',
};

/** Hebrew weekday names, indexed the way `Date.getDay()` counts. */
const WEEKDAY_NAMES = [
  'יום ראשון',
  'יום שני',
  'יום שלישי',
  'יום רביעי',
  'יום חמישי',
  'יום שישי',
  'שבת',
] as const;

/**
 * Unit time, not machine time — a status set in Israel has to read the same on
 * a laptop parked in another zone.
 */
const zoneFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_CONFIG.timeZone,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

interface ZonedParts {
  weekday: number;
  year: number;
  month: number;
  day: number;
  hour: string;
  minute: string;
  /** Days since the epoch, so two moments can be compared by calendar day. */
  dayIndex: number;
}

function zonedParts(timestamp: number): ZonedParts {
  const parts = Object.fromEntries(
    zoneFormatter.formatToParts(new Date(timestamp)).map((part) => [part.type, part.value]),
  ) as Record<string, string>;

  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);

  return {
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
    year,
    month,
    day,
    hour: parts.hour,
    minute: parts.minute,
    dayIndex: Math.floor(Date.UTC(year, month - 1, day) / 86_400_000),
  };
}

/**
 * How an open-ended status closes its sentence — "בגימלים עד להודעה חדשה".
 * It is the same "עד" the dated form uses, so the badge reads as one line
 * either way.
 */
export const INDEFINITE_STATUS_TEXT = 'עד להודעה חדשה';

/**
 * True for a status that was set without an end date. Such a status never
 * lapses on its own; only a commander clears it.
 */
export function isIndefiniteStatus(status: TimedStatus | null | undefined): boolean {
  return Boolean(status?.kind) && !String(status?.until ?? '').trim();
}

/**
 * A dated status counts while `until` is still in the future; an open-ended
 * one counts until it is changed. A deadline that cannot be read at all is
 * treated as lapsed — a corrupt record must not pin a soldier down.
 */
export function isTimedStatusActive(
  status: TimedStatus | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!status?.kind) return false;
  if (isIndefiniteStatus(status)) return true;
  const until = Date.parse(String(status.until));
  return Number.isFinite(until) && until > now;
}

/**
 * When the status lapses, in Hebrew and relative to the reader:
 * `היום 08:00`, `מחר 08:00`, `יום שלישי 08:00`, or `02/07 08:00` further out.
 */
export function formatStatusDeadline(
  until: string | number | null | undefined,
  now: number = Date.now(),
): string | null {
  const timestamp = typeof until === 'number' ? until : Date.parse(String(until ?? ''));
  if (!Number.isFinite(timestamp)) return null;

  const target = zonedParts(timestamp);
  const today = zonedParts(now);
  const clock = `${target.hour}:${target.minute}`;
  const daysAway = target.dayIndex - today.dayIndex;

  if (daysAway === 0) return `היום ${clock}`;
  if (daysAway === 1) return `מחר ${clock}`;
  if (daysAway > 1 && daysAway < 7) return `${WEEKDAY_NAMES[target.weekday]} ${clock}`;

  const date = `${String(target.day).padStart(2, '0')}/${String(target.month).padStart(2, '0')}`;
  return `${date} ${clock}`;
}

/**
 * The tail of a status sentence — `עד יום שלישי 08:00` for a dated status,
 * `עד להודעה חדשה` for one set without an end. Everything that spells a status
 * out loud goes through here, so the open-ended case can never leak a `null`
 * into a sentence.
 */
export function formatStatusHorizon(
  until: string | number | null | undefined,
  now: number = Date.now(),
): string {
  const deadline = formatStatusDeadline(until, now);
  return deadline ? `עד ${deadline}` : INDEFINITE_STATUS_TEXT;
}

/**
 * The full sentence for an active timed status, e.g. `בבית עד יום שלישי 08:00`
 * or `בגימלים עד להודעה חדשה`. Returns `null` once the status has lapsed, so
 * the caller falls back to the plain availability label.
 */
export function formatTimedStatus(
  status: TimedStatus | null | undefined,
  now: number = Date.now(),
): string | null {
  if (!status || !isTimedStatusActive(status, now)) return null;
  return `${TIMED_STATUS_LABELS[status.kind]} ${formatStatusHorizon(status.until, now)}`;
}

/** The availability a soldier is actually in, timed status included. */
export function effectiveAvailability(
  soldier: Soldier,
  now: number = Date.now(),
): SoldierAvailability {
  if (isTimedStatusActive(soldier.temporaryStatus, now)) {
    return TIMED_STATUS_AVAILABILITY[(soldier.temporaryStatus as TimedStatus).kind];
  }
  return soldier.availability;
}

/** One line for the roster card — the timed sentence, or the plain label. */
export function describeSoldierStatus(soldier: Soldier, now: number = Date.now()): string {
  return (
    formatTimedStatus(soldier.temporaryStatus, now) ??
    SOLDIER_AVAILABILITY_LABELS[soldier.availability]
  );
}

/** The glyph that line leads with — the timed one while it is still running. */
export function soldierStatusIcon(soldier: Soldier, now: number = Date.now()): string {
  if (isTimedStatusActive(soldier.temporaryStatus, now)) {
    return TIMED_STATUS_ICONS[(soldier.temporaryStatus as TimedStatus).kind];
  }
  return AVAILABILITY_ICONS[effectiveAvailability(soldier, now)];
}

/**
 * How far the unit's clock runs ahead of UTC at a given moment, in
 * milliseconds — read from the zone itself, so a summer deadline and a winter
 * one are each stamped with their own offset.
 */
function zoneOffset(timestamp: number): number {
  const parts = zonedParts(timestamp);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    Number(parts.hour),
    Number(parts.minute),
  );
  // The formatter drops the seconds, so the moment is compared to the minute.
  return asUtc - Math.floor(timestamp / 60_000) * 60_000;
}

/**
 * The deadline a one-tap status is given when nobody typed one: the next
 * 08:00 in unit time — today's if the morning has not come yet, tomorrow's
 * once it has.
 *
 * This is what makes "בבית" a single tap instead of a form: the status still
 * expires by itself, and a manager who needs another hour sets it from the
 * full status dialog. The offset is read twice so a deadline that lands the
 * other side of a clock change is still 08:00 on the ground.
 */
export function nextMorningDeadline(now: number = Date.now(), hour: number = 8): string {
  const parts = zonedParts(now);
  const shift = Number(parts.hour) < hour ? 0 : 1;
  const wall = Date.UTC(parts.year, parts.month - 1, parts.day + shift, hour, 0);
  const first = wall - zoneOffset(now);
  return new Date(wall - zoneOffset(first)).toISOString();
}

/**
 * Builds a timed status from the form fields, or `null` to clear one.
 *
 * The kind is the only thing required: it is what the manager actually chose.
 * A blank "עד מתי" builds the open-ended status rather than refusing to build
 * anything, and a deadline that cannot be parsed is read the same way — losing
 * the whole status because the picker handed back something unreadable would
 * be worse than losing the date.
 */
export function buildTimedStatus(
  kind: TimedStatusKind | '',
  until: string | null | undefined,
  note?: string,
): TimedStatus | null {
  if (!kind) return null;
  const typed = String(until ?? '').trim();
  const timestamp = typed ? Date.parse(typed) : Number.NaN;
  const built: TimedStatus = {
    kind,
    until: Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null,
  };
  if (note?.trim()) built.note = note.trim();
  return built;
}
